import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiClock, FiX, FiCheck, FiChevronDown } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { CATEGORIAS, metaCategoria } from '../servicios/categorias';

/**
 * Selector de servicios del cliente: busca, agrupa por categoría y permite
 * quedarse solo con lo ya seleccionado cuando el catálogo se hace largo.
 * Cada servicio es una tarjeta marcable, no una fila de tabla.
 *
 * Las categorías son acordeones porque un catálogo grande alargaba la ficha
 * varias pantallas. Arrancan cerradas salvo las que ya tienen algo marcado, y
 * al buscar o filtrar se abren todas: si no, parecería que no hay resultados.
 */
const AsignadorServicios = ({ catalogo, seleccionados, onAlternar }) => {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const [soloSeleccionados, setSoloSeleccionados] = useState(false);

  const marcados = useMemo(() => new Set(seleccionados), [seleccionados]);

  // Se calcula solo al montar: a partir de ahí manda lo que abra o cierre la persona
  const [abiertos, setAbiertos] = useState(() => new Set(
    catalogo.filter((s) => seleccionados.includes(s.id)).map((s) => s.categoria)
  ));

  const alternarGrupo = (categoria) => setAbiertos((prev) => {
    const copia = new Set(prev);
    if (copia.has(categoria)) copia.delete(categoria);
    else copia.add(categoria);
    return copia;
  });

  // Con un filtro puesto, lo que quede visible debe verse sin tener que abrir
  const forzarAbiertos = busqueda.trim() !== '' || soloSeleccionados;

  const grupos = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    const filtrados = catalogo.filter((servicio) => {
      if (soloSeleccionados && !marcados.has(servicio.id)) return false;
      if (!texto) return true;
      return servicio.titulo.toLowerCase().includes(texto)
        || metaCategoria(servicio.categoria).label.toLowerCase().includes(texto);
    });

    // Orden fijo de categorías para que la lista no baile entre búsquedas
    return CATEGORIAS
      .map((categoria) => ({
        categoria,
        ...metaCategoria(categoria),
        servicios: filtrados.filter((s) => s.categoria === categoria),
      }))
      .filter((grupo) => grupo.servicios.length > 0);
  }, [catalogo, busqueda, soloSeleccionados, marcados]);

  const totalHoras = useMemo(() => (
    catalogo.filter((s) => marcados.has(s.id)).reduce((acc, s) => acc + (s.horas || 0), 0)
  ), [catalogo, marcados]);

  if (catalogo.length === 0) {
    return (
      <p className="pu-srv-vacio">
        {t('perfilUsuario.sinServiciosEnCatalogo')}{' '}
        <Link to="/dashboard/gestion-servicios">{t('perfilUsuario.crearElPrimero')}</Link>.
      </p>
    );
  }

  return (
    <div className="pu-srv">
      <div className="pu-srv-buscador">
        <FiSearch />
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder={t('perfilUsuario.buscarServicio')}
          aria-label={t('perfilUsuario.buscarServicioAria')}
        />
        {busqueda && (
          <button
            type="button"
            className="pu-srv-limpiar"
            onClick={() => setBusqueda('')}
            title={t('perfilUsuario.limpiarBusqueda')}
            aria-label={t('perfilUsuario.limpiarBusqueda')}
          >
            <FiX />
          </button>
        )}
      </div>

      <div className="pu-srv-meta">
        <button
          type="button"
          className={`pu-srv-filtro ${soloSeleccionados ? 'activo' : ''}`}
          onClick={() => setSoloSeleccionados((v) => !v)}
          aria-pressed={soloSeleccionados}
        >
          {t('perfilUsuario.soloSeleccionados')}
        </button>
        <span className="pu-srv-contador">
          {t('perfilUsuario.deSeleccionados', { seleccionados: seleccionados.length, total: catalogo.length })}
          {totalHoras > 0 && <> · <FiClock /> {totalHoras} h</>}
        </span>
      </div>

      {grupos.length === 0 ? (
        <p className="pu-srv-vacio">
          {soloSeleccionados
            ? t('perfilUsuario.sinSeleccionados')
            : t('perfilUsuario.sinCoincidenciasServicio')}
        </p>
      ) : grupos.map((grupo) => {
        const IconoCat = grupo.icon;
        const abierto = forzarAbiertos || abiertos.has(grupo.categoria);
        const marcadosGrupo = grupo.servicios.filter((s) => marcados.has(s.id)).length;
        return (
          <div key={grupo.categoria} className={`pu-srv-grupo pu-srv-grupo--${grupo.categoria} ${abierto ? 'pu-srv-grupo--abierto' : ''}`}>
            <button
              type="button"
              className="pu-srv-grupo-titulo"
              onClick={() => alternarGrupo(grupo.categoria)}
              aria-expanded={abierto}
              aria-controls={`pu-srv-grupo-${grupo.categoria}`}
            >
              <FiChevronDown className="pu-srv-grupo-flecha" aria-hidden="true" />
              <IconoCat aria-hidden="true" /> {grupo.label}
              <span>{marcadosGrupo > 0 ? `${marcadosGrupo}/${grupo.servicios.length}` : grupo.servicios.length}</span>
            </button>

            <div className="pu-srv-grid" id={`pu-srv-grupo-${grupo.categoria}`} hidden={!abierto}>
              {grupo.servicios.map((servicio) => {
                const marcado = marcados.has(servicio.id);
                return (
                  <label
                    key={servicio.id}
                    className={`pu-srv-card ${marcado ? 'marcado' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => onAlternar(servicio.id)}
                    />
                    <span className="pu-srv-check" aria-hidden="true"><FiCheck /></span>

                    <span className="pu-srv-card-info">
                      <span className="pu-srv-card-titulo">{servicio.titulo}</span>
                      {servicio.descripcion?.length > 0 && (
                        <span className="pu-srv-card-desc">
                          {servicio.descripcion.join(' · ')}
                        </span>
                      )}
                    </span>

                    {servicio.horas !== null && typeof servicio.horas !== 'undefined' && (
                      <span className="pu-srv-card-horas"><FiClock /> {servicio.horas} h</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AsignadorServicios;
